import { useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type ReactNode, type SetStateAction } from "react";
import { DndContext, PointerSensor, closestCenter, useDraggable, useDroppable, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { ArrowRight, CalendarClock, CheckCircle2, ChevronDown, Columns3, GripVertical, List, LockKeyhole, Plus, Search, SlidersHorizontal, UserRound, X } from "lucide-react";
import type { AgentProposal, BoardSnapshot, WorkAgentState, WorkItem, WorkStatus } from "@business-ops/domain";
import { ACTIVE_STATUSES, STATUS_LABELS, clientColor, clientName, dueState, formatDeadline, memberLabel, memberName, projectName } from "../helpers.js";
import { Avatar, ColorMark, EmptyState } from "../components/ui.js";
import { SafeLinkedText } from "../components/safe-linked-text.js";
import { approvalUnits, proposalAttentionKind, type AgentWorkQueue } from "./review.js";
import { currentWorkMutationFeedback, type WorkMutationFeedback } from "../work-mutation-feedback.js";

export type WorkLens = "board" | "timeline" | "table";
export type WorkApprovalFilter = "ALL" | "PENDING";
export type TimelineZoom = "day" | "week" | "month" | "quarter";
export type TimelineGroupBy = "none" | "client" | "project" | "owner" | "status";
type WorkMode = "action" | "waiting" | "all";
export type WorkLaneId = "todo" | "doing" | "review" | "blocked" | "done";

export interface WorkViewState {
  query: string;
  lens: WorkLens;
  assignee: string;
  mobileLane: WorkLaneId;
  showCompleted: boolean;
  approval: WorkApprovalFilter;
  focusedWorkItemId: string | null;
  timelineZoom: TimelineZoom;
  timelineGroupBy: TimelineGroupBy;
}

export const DEFAULT_WORK_VIEW_STATE: WorkViewState = {
  query: "",
  lens: "board",
  assignee: "ALL",
  mobileLane: "doing",
  showCompleted: false,
  approval: "ALL",
  focusedWorkItemId: null,
  timelineZoom: "week",
  timelineGroupBy: "none"
};

export type WorkViewStateSetter = Dispatch<SetStateAction<WorkViewState>>;

export interface WorkLane {
  id: WorkLaneId;
  label: string;
  statuses: readonly WorkStatus[];
  dropStatus: WorkStatus;
}

// WHY [BOL-UX-LANES-001]: the domain keeps seven precise workflow states, but
// seven simultaneous columns made the board difficult to scan. These five
// visual lanes preserve every state in cards and the editor while reducing the
// owner's first-level choices. Never collapse the underlying stored status.
export const WORK_LANES: readonly WorkLane[] = [
  { id: "todo", label: "To do", statuses: ["BACKLOG", "READY"], dropStatus: "READY" },
  { id: "doing", label: "In progress", statuses: ["IN_PROGRESS"], dropStatus: "IN_PROGRESS" },
  { id: "review", label: "Review", statuses: ["INTERNAL_REVIEW", "CLIENT_REVIEW"], dropStatus: "INTERNAL_REVIEW" },
  { id: "blocked", label: "Blocked", statuses: ["BLOCKED"], dropStatus: "BLOCKED" },
  { id: "done", label: "Done", statuses: ["DONE"], dropStatus: "DONE" }
];

export function workLaneAfterNavigationKey(
  lanes: readonly WorkLaneId[],
  current: WorkLaneId,
  key: string
): WorkLaneId | null {
  const index = lanes.indexOf(current);
  if (index < 0 || lanes.length === 0) return null;
  if (key === "Home") return lanes[0] ?? null;
  if (key === "End") return lanes.at(-1) ?? null;
  if (key === "ArrowRight") return lanes[(index + 1) % lanes.length] ?? null;
  if (key === "ArrowLeft") return lanes[(index - 1 + lanes.length) % lanes.length] ?? null;
  return null;
}

// WHY [BOL-WORK-MOVE-002]: the responsive board hides non-selected lanes, so
// drag alone cannot move a phone task to them. The visible native selector is
// keyboard and single-pointer operable and delegates to the same canonical
// moveWork path as desktop drag. Falsifier: <=768px offers drag as the only
// move path, or choosing a status bypasses moveWork or creates local state.
export async function moveWorkToStatus(item: WorkItem, status: WorkStatus, moveWork: (item: WorkItem, status: WorkStatus) => Promise<void>): Promise<void> {
  if (status === item.status) return;
  await moveWork(item, status);
}

export function isPendingWorkApproval(item: WorkItem): boolean {
  return item.approvalStatus === "INTERNAL_PENDING" || item.approvalStatus === "CLIENT_PENDING";
}

// WHY [BOL-ACTIVE-APPROVAL-WORK-REVISION-001]: /api/review and /api/board
// are independent authenticated reads, but Work freshness belongs to the
// server-declared approval unit. An existing-Work Task + Message bundle reviews
// its current BOARD_UPDATE at vN while the linked Send correctly names that
// proposal's future vN+1 result. Admit that future revision only with the exact
// complete/current bundle; a standalone, unrelated, or stale vN+1 row remains
// hidden. New Work has no row yet and remains visible. Falsifier: either half of
// the valid bundle disappears, the two rows cease to be one approvable unit, or
// changing/missing Work lets any member of the stale bundle render an action.
export function canonicalActiveAgentProposals(
  board: BoardSnapshot,
  proposals: readonly AgentProposal[]
): AgentProposal[] {
  const workVersions = new Map(board.workItems.map((item) => [item.id, item.version]));
  const matchesCurrentWork = (proposal: AgentProposal) => proposal.workItemId === null
    || (proposal.expectedVersions.workItemVersion !== null
      && workVersions.get(proposal.workItemId) === proposal.expectedVersions.workItemVersion);
  const acceptedIds = new Set<string>();

  for (const unit of approvalUnits(proposals)) {
    if (!unit.bundleId || !unit.complete) {
      for (const proposal of unit.proposals.filter(matchesCurrentWork)) acceptedIds.add(proposal.id);
      continue;
    }
    if (unit.proposals.every(matchesCurrentWork)
      || isCurrentExistingWorkCompoundBundle(workVersions, unit.proposals)) {
      for (const proposal of unit.proposals) acceptedIds.add(proposal.id);
    }
  }
  return proposals.filter((proposal) => acceptedIds.has(proposal.id));
}

function isCurrentExistingWorkCompoundBundle(
  workVersions: ReadonlyMap<string, number>,
  proposals: readonly AgentProposal[]
): boolean {
  const [workProposal, messageProposal] = proposals;
  if (proposals.length !== 2
    || !workProposal
    || !messageProposal
    || workProposal.effect.kind !== "BOARD_UPDATE"
    || messageProposal.effect.kind !== "SEND_MESSAGE"
    || !messageProposal.effect.schedule
    || !messageProposal.effect.workDependency
    || workProposal.workItemId === null) return false;

  const workItemId = workProposal.workItemId;
  const currentVersion = workVersions.get(workItemId);
  const dependency = messageProposal.effect.workDependency;
  return currentVersion !== undefined
    && workProposal.effect.workItemId === workItemId
    && workProposal.effect.expectedVersion === currentVersion
    && workProposal.expectedVersions.workItemVersion === currentVersion
    && messageProposal.workItemId === workItemId
    && messageProposal.effect.schedule.workItemId === workItemId
    && dependency.proposalId === workProposal.id
    && dependency.workItemId === workItemId
    && dependency.workItemVersion === currentVersion + 1
    && messageProposal.expectedVersions.workItemVersion === dependency.workItemVersion;
}

export function filteredWorkItems(board: BoardSnapshot, state: WorkViewState): WorkItem[] {
  const normalizedQuery = state.query.trim().toLowerCase();
  return board.workItems.filter((item) => {
    if (item.status === "ARCHIVED") return false;
    if ((item.workKind ?? "TASK") !== "TASK") return false;
    if (!state.showCompleted && item.status === "DONE") return false;
    if (state.approval === "PENDING" && !isPendingWorkApproval(item)) return false;
    if (state.assignee !== "ALL" && item.assigneeId !== state.assignee) return false;
    if (!normalizedQuery) return true;
    const haystack = [item.title, item.description, clientName(board, item), projectName(board, item) ?? "", memberName(board, item.assigneeId)].join(" ").toLowerCase();
    return haystack.includes(normalizedQuery);
  });
}

// WHY [BOL-TASKS-LENSES-001]: Board, Timeline, and the legacy disclosed Table
// are projections of one BoardSnapshot, not independent interactive stores.
// A parent-owned view state lets Chat remain mounted while Back preserves the
// exact lens, query, assignee, compact lane, approval/completed filters, and
// focused identity. Falsifier:
// either lens shows a different Work ID/filter result or Back resets Tasks.
export function WorkView({ board, openWork, moveWork, addWork, agentQueue, embedded = false, viewState, setViewState, mutationFeedback = [] }: {
  board: BoardSnapshot;
  openWork: (item: WorkItem) => void;
  moveWork: (item: WorkItem, status: WorkStatus) => Promise<void>;
  addWork: () => void;
  agentQueue: AgentWorkQueue;
  embedded?: boolean;
  viewState?: WorkViewState;
  setViewState?: WorkViewStateSetter;
  mutationFeedback?: readonly WorkMutationFeedback[];
}) {
  const [localViewState, setLocalViewState] = useState<WorkViewState>(DEFAULT_WORK_VIEW_STATE);
  const currentViewState = viewState ?? localViewState;
  const updateViewState = setViewState ?? setLocalViewState;
  const { query, lens, assignee, mobileLane, showCompleted, approval, timelineZoom, timelineGroupBy, focusedWorkItemId } = currentViewState;
  const searchInputRef = useRef<HTMLInputElement>(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
  const lanes = showCompleted ? WORK_LANES : WORK_LANES.filter((lane) => lane.id !== "done");
  const taskItems = board.workItems.filter((item) => (item.workKind ?? "TASK") === "TASK");
  const completedCount = taskItems.filter((item) => item.status === "DONE").length;
  const workAgentStates = board.workAgentStates ?? [];
  const activeCount = taskItems.filter((item) => !["DONE", "ARCHIVED"].includes(item.status)).length;
  const toggleCompleted = () => {
    updateViewState((current) => ({
      ...current,
      mobileLane: current.showCompleted && current.mobileLane === "done" ? "doing" : current.mobileLane,
      showCompleted: !current.showCompleted
    }));
  };
  const handleLaneKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, laneId: WorkLaneId) => {
    const next = workLaneAfterNavigationKey(lanes.map((lane) => lane.id), laneId, event.key);
    if (!next) return;
    // WHY [BOL-MOBILE-STATUS-TABS-001]: the compact board exposes one lane at
    // a time, so its status switcher follows the standard horizontal tab keys
    // and brings the next tab into view. A hidden swipe-only lane is not an
    // acceptable control. Falsifier: ArrowLeft/Right changes neither focus nor
    // the visible lane, or the selected tab remains clipped at 360x668.
    event.preventDefault();
    updateViewState((current) => ({ ...current, mobileLane: next }));
    const target = event.currentTarget.parentElement?.querySelector<HTMLButtonElement>(`[data-work-lane="${next}"]`);
    target?.focus();
    target?.scrollIntoView({ block: "nearest", inline: "nearest" });
  };
  const visible = useMemo(() => filteredWorkItems(board, currentViewState), [board, currentViewState]);
  const openSelectedWork = (item: WorkItem) => {
    updateViewState((current) => ({ ...current, focusedWorkItemId: item.id }));
    openWork(item);
  };

  const handleDragEnd = async ({ active, over }: DragEndEvent) => {
    if (!over) return;
    const item = board.workItems.find((candidate) => candidate.id === active.id);
    const lane = WORK_LANES.find((candidate) => `lane:${candidate.id}` === over.id);
    if (!item || !lane || lane.statuses.includes(item.status)) return;
    await moveWork(item, lane.dropStatus);
  };

  return (
    <div className={`view-stack view-stack--work ${embedded ? "view-stack--embedded-tool" : ""}`}>
      <h1 className="sr-only">Tasks</h1>
      {!embedded ? <section className="work-focus-heading">
        <div><p className="eyebrow">Tasks</p><h2>{activeCount} active {activeCount === 1 ? "task" : "tasks"}</h2><p>One task set, organized by status or time.</p></div>
      </section> : null}
      <>
          <div className="work-lensbar">
            <div className="segmented-control" role="group" aria-label="Tasks view">
              <button type="button" aria-pressed={lens === "board"} className={lens === "board" ? "is-active" : ""} onClick={() => updateViewState((current) => ({ ...current, lens: "board" }))}><Columns3 size={17} />Board</button>
              <button type="button" aria-pressed={lens === "timeline"} className={lens === "timeline" ? "is-active" : ""} onClick={() => updateViewState((current) => ({ ...current, lens: "timeline" }))}><CalendarClock size={17} />Timeline</button>
            </div>
            <div className="work-lensbar__status">
              <p>{visible.length} shown from the canonical task store</p>
              {approval === "PENDING" ? <button type="button" onClick={() => updateViewState((current) => ({ ...current, approval: "ALL" }))} aria-label="Clear Pending task filter">Pending approvals <X size={15} aria-hidden="true" /></button> : null}
            </div>
          </div>
          <details className="work-board-tools">
            <summary><span><SlidersHorizontal size={17} />Search or change view</span><ChevronDown size={17} /></summary>
            <div className="work-board-tools__body">
              <section className="work-toolbar" aria-label="Work filters">
                <div className="search-box"><Search size={18} aria-hidden="true" /><label className="sr-only" htmlFor="work-search">Search work</label><input ref={searchInputRef} id="work-search" value={query} onChange={(event) => { const value = event.currentTarget.value; updateViewState((current) => ({ ...current, query: value })); }} placeholder="Search work, client, or person" />{query ? <button type="button" aria-label="Clear" onClick={() => { updateViewState((current) => ({ ...current, query: "" })); searchInputRef.current?.focus(); }}><X size={17} aria-hidden="true" /></button> : null}</div>
                <label className="select-filter"><UserRound size={17} /><span className="sr-only">Filter by assignee</span><select value={assignee} onChange={(event) => { const value = event.currentTarget.value; updateViewState((current) => ({ ...current, assignee: value })); }}><option value="ALL">Everyone</option>{board.members.map((member) => <option value={member.id} key={member.id}>{memberLabel(board.members, member)}</option>)}</select></label>
              </section>
              <section className="work-commandbar" aria-label="Work controls">
                <div className="work-commandbar__count"><p><strong>{visible.length}</strong> visible {visible.length === 1 ? "item" : "items"}</p><button type="button" aria-pressed={showCompleted} onClick={toggleCompleted}><CheckCircle2 size={16} />Completed {completedCount}</button><button type="button" onClick={addWork}><Plus size={16} />Add work</button></div>
                <button className="work-table-toggle" type="button" aria-pressed={lens === "table"} onClick={() => updateViewState((current) => ({ ...current, lens: "table" }))}><List size={17} />Table view</button>
              </section>
            </div>
          </details>

          {lens === "board" ? (
            <>
              <div className="mobile-status-tabs" role="tablist" aria-label="Work status">
                {lanes.map((lane) => <button type="button" role="tab" id={`work-status-tab-${lane.id}`} data-work-lane={lane.id} aria-controls={`work-status-panel-${lane.id}`} aria-selected={mobileLane === lane.id} tabIndex={mobileLane === lane.id ? 0 : -1} className={mobileLane === lane.id ? "is-active" : ""} onClick={() => updateViewState((current) => ({ ...current, mobileLane: lane.id }))} onKeyDown={(event) => handleLaneKeyDown(event, lane.id)} key={lane.id}>{lane.label} <span>{visible.filter((item) => lane.statuses.includes(item.status)).length}</span></button>)}
              </div>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(event) => void handleDragEnd(event)}>
                <div className={`board-columns ${showCompleted ? "has-completed" : ""}`}>
                  {lanes.map((lane) => (
                    <WorkColumn key={lane.id} lane={lane} items={visible.filter((item) => lane.statuses.includes(item.status))} board={board} openWork={openSelectedWork} moveWork={moveWork} mobileActive={mobileLane === lane.id} proposals={agentQueue.proposals} agentStates={workAgentStates} focusedWorkItemId={focusedWorkItemId} mutationFeedback={mutationFeedback} />
                  ))}
                </div>
              </DndContext>
            </>
          ) : lens === "timeline" ? (
            <WorkTimeline items={visible} board={board} openWork={openSelectedWork} moveWork={moveWork} proposals={agentQueue.proposals} agentStates={workAgentStates} zoom={timelineZoom} groupBy={timelineGroupBy} setZoom={(zoom) => updateViewState((current) => ({ ...current, timelineZoom: zoom }))} setGroupBy={(groupBy) => updateViewState((current) => ({ ...current, timelineGroupBy: groupBy }))} focusedWorkItemId={focusedWorkItemId} mutationFeedback={mutationFeedback} />
          ) : <WorkTable items={visible} board={board} openWork={openSelectedWork} proposals={agentQueue.proposals} agentStates={workAgentStates} focusedWorkItemId={focusedWorkItemId} mutationFeedback={mutationFeedback} />}
      </>
    </div>
  );
}

export const TIMELINE_CLIENT_COLORS = [
  "#9d4f35",
  "#356d5d",
  "#5f628e",
  "#9a7028",
  "#3f6d91",
  "#7d4f69",
  "#647843",
  "#7c5a43"
] as const;

// WHY [BOL-TIMELINE-CLIENT-COLOR-001]: Timeline needs a stable visual scan cue
// without embedding client/project names or a second editable color store.
// Hash canonical identity into a fixed accessible palette and always render its
// text beside it. Falsifier: refresh changes color, or color is the only way to
// identify a client/project.
export function timelineClientColor(clientId: string | null, projectId: string | null = null): string {
  const identity = projectId ? `project:${projectId}` : clientId ? `client:${clientId}` : null;
  if (!identity) return "#76786f";
  let hash = 2_166_136_261;
  for (const character of identity) {
    hash ^= character.charCodeAt(0);
    hash = Math.imul(hash, 16_777_619);
  }
  return TIMELINE_CLIENT_COLORS[(hash >>> 0) % TIMELINE_CLIENT_COLORS.length]!;
}

export interface TimelineScaleTick {
  ordinal: number;
  position: number;
  label: string;
}

export interface TimelineScale {
  startOrdinal: number;
  endOrdinal: number;
  dayCount: number;
  todayOrdinal: number;
  todayPosition: number;
  contentWidth: number;
  zoom: TimelineZoom;
  ticks: TimelineScaleTick[];
}

export interface TimelineRangePlacement {
  startPosition: number;
  width: number;
  clippedStart: boolean;
  clippedEnd: boolean;
  outlier: "BEFORE" | "AFTER" | null;
}

export interface TimelineGroup {
  id: string;
  label: string;
  items: WorkItem[];
}

const TIMELINE_ZOOM_CONFIG: Readonly<Record<TimelineZoom, { minimumDays: number; paddingDays: number; pixelsPerDay: number; tickDays: number }>> = {
  day: { minimumDays: 14, paddingDays: 2, pixelsPerDay: 52, tickDays: 1 },
  week: { minimumDays: 56, paddingDays: 7, pixelsPerDay: 14, tickDays: 7 },
  month: { minimumDays: 183, paddingDays: 31, pixelsPerDay: 4.5, tickDays: 31 },
  quarter: { minimumDays: 550, paddingDays: 92, pixelsPerDay: 1.5, tickDays: 92 }
};

// WHY [BOL-TIMELINE-RANGE-001]: a Notion-style Timeline is a range view, not
// a due-date milestone projection. Only canonical taskStartAt/taskEndAt may
// create a bar; both endpoints are inclusive local days. Falsifier: dueAt or
// createdAt invents a span, an end day disappears, or zoom hides a stored range.
export function timelineScale(items: readonly WorkItem[], timeZone: string, zoom: TimelineZoom = "week", now = new Date()): TimelineScale {
  const config = TIMELINE_ZOOM_CONFIG[zoom];
  const today = localDayOrdinal(now, timeZone);
  // Keep zoom truthful when one imported Task has a distant range. The fixed
  // Today-led window clips and labels that outlier instead of shrinking every
  // ordinary day; items still supply the canonical rows and never the scale.
  void items;
  const rawStart = today - config.paddingDays;
  const desiredEnd = rawStart + config.minimumDays - 1;
  const startOrdinal = alignTimelineStart(rawStart, zoom);
  const endOrdinal = alignTimelineEnd(desiredEnd, zoom);
  const dayCount = endOrdinal - startOrdinal + 1;
  const todayPosition = ((today - startOrdinal) + 0.5) / dayCount * 100;
  const tickOrdinals: number[] = [];
  for (let ordinal = firstTimelineTick(startOrdinal, zoom); ordinal <= endOrdinal; ordinal = nextTimelineTick(ordinal, zoom, config.tickDays)) {
    tickOrdinals.push(ordinal);
  }
  const ticks = tickOrdinals.map((ordinal) => ({
    ordinal,
    position: (ordinal - startOrdinal) / dayCount * 100,
    label: timelineOrdinalLabel(ordinal, zoom)
  }));
  return {
    startOrdinal,
    endOrdinal,
    dayCount,
    todayOrdinal: today,
    todayPosition,
    contentWidth: Math.max(720, Math.round(dayCount * config.pixelsPerDay)),
    zoom,
    ticks
  };
}

export function timelineRangePlacement(item: Pick<WorkItem, "taskStartAt" | "taskEndAt">, timeZone: string, scale: TimelineScale): TimelineRangePlacement | null {
  if (!item.taskStartAt || !item.taskEndAt) return null;
  const startOrdinal = localDayOrdinal(new Date(item.taskStartAt), timeZone);
  const endOrdinal = localDayOrdinal(new Date(item.taskEndAt), timeZone);
  if (endOrdinal < scale.startOrdinal) {
    return { startPosition: 0, width: 0.35, clippedStart: true, clippedEnd: true, outlier: "BEFORE" };
  }
  if (startOrdinal > scale.endOrdinal) {
    return { startPosition: 100, width: 0.35, clippedStart: true, clippedEnd: true, outlier: "AFTER" };
  }
  const clippedStart = Math.max(scale.startOrdinal, startOrdinal);
  const clippedEnd = Math.min(scale.endOrdinal, endOrdinal);
  return {
    startPosition: (clippedStart - scale.startOrdinal) / scale.dayCount * 100,
    width: Math.max(0.35, (clippedEnd - clippedStart + 1) / scale.dayCount * 100),
    clippedStart: clippedStart !== startOrdinal,
    clippedEnd: clippedEnd !== endOrdinal,
    outlier: null
  };
}

export function timelineGroups(items: readonly WorkItem[], board: BoardSnapshot, groupBy: TimelineGroupBy): TimelineGroup[] {
  const sorted = [...items].sort((left, right) => {
    const leftTime = left.taskStartAt ? Date.parse(left.taskStartAt) : Number.MAX_SAFE_INTEGER;
    const rightTime = right.taskStartAt ? Date.parse(right.taskStartAt) : Number.MAX_SAFE_INTEGER;
    return leftTime - rightTime || left.sortOrder - right.sortOrder || left.title.localeCompare(right.title) || left.id.localeCompare(right.id);
  });
  if (groupBy === "none") return [{ id: "all", label: "All tasks", items: sorted }];
  const grouped = new Map<string, { label: string; items: WorkItem[] }>();
  for (const item of sorted) {
    const identity = groupBy === "client" ? item.clientId
      : groupBy === "project" ? item.projectId
        : groupBy === "owner" ? item.assigneeId
          : item.status;
    const id = identity ?? "unassigned";
    const label = groupBy === "client" ? clientName(board, item)
      : groupBy === "project" ? projectName(board, item) ?? "No project"
        : groupBy === "owner" ? memberName(board, item.assigneeId)
          : STATUS_LABELS[item.status];
    const current = grouped.get(id) ?? { label, items: [] };
    current.items.push(item);
    grouped.set(id, current);
  }
  return [...grouped.entries()]
    .map(([id, value]) => ({ id, ...value }))
    .sort((left, right) => left.label.localeCompare(right.label) || left.id.localeCompare(right.id));
}

export function scrollTimelineToToday(viewport: HTMLElement | null): boolean {
  const marker = viewport?.querySelector<HTMLElement>(".timeline-axis__today");
  if (!marker) return false;
  marker.scrollIntoView({ behavior: "auto", block: "nearest", inline: "center" });
  return true;
}

function alignTimelineStart(ordinal: number, zoom: TimelineZoom): number {
  const date = new Date(ordinal * 86_400_000);
  if (zoom === "week") return ordinal - ((date.getUTCDay() + 6) % 7);
  if (zoom === "month" || zoom === "quarter") {
    const month = zoom === "quarter" ? Math.floor(date.getUTCMonth() / 3) * 3 : date.getUTCMonth();
    return Math.floor(Date.UTC(date.getUTCFullYear(), month, 1) / 86_400_000);
  }
  return ordinal;
}

function alignTimelineEnd(ordinal: number, zoom: TimelineZoom): number {
  const date = new Date(ordinal * 86_400_000);
  if (zoom === "week") return ordinal + (6 - ((date.getUTCDay() + 6) % 7));
  if (zoom === "month" || zoom === "quarter") {
    const month = zoom === "quarter" ? Math.floor(date.getUTCMonth() / 3) * 3 + 3 : date.getUTCMonth() + 1;
    return Math.floor(Date.UTC(date.getUTCFullYear(), month, 1) / 86_400_000) - 1;
  }
  return ordinal;
}

function firstTimelineTick(startOrdinal: number, zoom: TimelineZoom): number {
  return alignTimelineStart(startOrdinal, zoom);
}

function nextTimelineTick(ordinal: number, zoom: TimelineZoom, fallbackDays: number): number {
  if (zoom === "month" || zoom === "quarter") {
    const date = new Date(ordinal * 86_400_000);
    const months = zoom === "quarter" ? 3 : 1;
    return Math.floor(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + months, 1) / 86_400_000);
  }
  return ordinal + fallbackDays;
}

function timelineOrdinalLabel(ordinal: number, zoom: TimelineZoom): string {
  const date = new Date(ordinal * 86_400_000);
  if (zoom === "quarter") return `Q${Math.floor(date.getUTCMonth() / 3) + 1} ${date.getUTCFullYear()}`;
  if (zoom === "month") return new Intl.DateTimeFormat(undefined, { timeZone: "UTC", month: "short", year: "numeric" }).format(date);
  return new Intl.DateTimeFormat(undefined, { timeZone: "UTC", month: "short", day: "numeric" }).format(date);
}

function localDayOrdinal(value: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).formatToParts(value);
  const part = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((entry) => entry.type === type)?.value ?? 0);
  return Math.floor(Date.UTC(part("year"), part("month") - 1, part("day")) / 86_400_000);
}

const APPROVAL_LABELS: Readonly<Record<WorkItem["approvalStatus"], string>> = {
  NOT_REQUIRED: "No approval needed",
  NOT_SUBMITTED: "Not submitted",
  INTERNAL_PENDING: "Internal approval",
  CLIENT_PENDING: "Client approval",
  APPROVED: "Approved",
  CHANGES_REQUESTED: "Changes requested"
};

function WorkTimeline({ items, board, openWork, moveWork, proposals, agentStates, zoom, groupBy, setZoom, setGroupBy, focusedWorkItemId, mutationFeedback }: {
  items: readonly WorkItem[];
  board: BoardSnapshot;
  openWork: (item: WorkItem) => void;
  moveWork: (item: WorkItem, status: WorkStatus) => Promise<void>;
  proposals: readonly AgentProposal[];
  agentStates: readonly WorkAgentState[];
  zoom: TimelineZoom;
  groupBy: TimelineGroupBy;
  setZoom: (zoom: TimelineZoom) => void;
  setGroupBy: (groupBy: TimelineGroupBy) => void;
  focusedWorkItemId: string | null;
  mutationFeedback: readonly WorkMutationFeedback[];
}) {
  const viewportRef = useRef<HTMLDivElement>(null);
  if (!items.length) return <EmptyState>No work matches these filters.</EmptyState>;
  const scale = timelineScale(items, board.workspace.timezone, zoom);
  const groups = timelineGroups(items, board, groupBy);
  const colorIdentities = [...new Map(items.map((item) => {
    const identity = item.projectId ? `project:${item.projectId}` : item.clientId ? `client:${item.clientId}` : "unassigned";
    return [identity, item] as const;
  })).values()];
  return (
    <section className="work-timeline" aria-label="Tasks timeline">
      <header className="work-timeline__header">
        <div><strong>Task ranges</strong><small>Open, edit, or move the same task IDs shown on Board.</small></div>
        <div className="work-timeline__controls">
          <button type="button" className="work-timeline__today" aria-label="Show Today in Timeline" onClick={() => scrollTimelineToToday(viewportRef.current)}>Today</button>
          <div className="work-timeline__zoom" role="group" aria-label="Timeline zoom">
            {(["day", "week", "month", "quarter"] as const).map((value) => <button type="button" aria-pressed={zoom === value} className={zoom === value ? "is-active" : ""} onClick={() => setZoom(value)} key={value}>{value[0]!.toUpperCase() + value.slice(1)}</button>)}
          </div>
          <label className="work-timeline__group"><span>Group</span><select value={groupBy} onChange={(event) => setGroupBy(event.currentTarget.value as TimelineGroupBy)}><option value="none">None</option><option value="client">Client</option><option value="project">Project</option><option value="owner">Owner</option><option value="status">Status</option></select></label>
        </div>
        <div className="work-timeline__legend" aria-label="Client and project colors">
          {colorIdentities.map((item) => {
            const identity = item.projectId ? `project:${item.projectId}` : item.clientId ? `client:${item.clientId}` : "unassigned";
            const label = projectName(board, item) ?? clientName(board, item);
            return <span key={identity}><ColorMark className="timeline-client-swatch" color={timelineClientColor(item.clientId, item.projectId)} />{label}</span>;
          })}
        </div>
      </header>
      <div ref={viewportRef} className="work-timeline__viewport" tabIndex={0} aria-label="Scrollable Task Timeline">
        <TimelineCanvas scale={scale}>
          <TimelineAxis scale={scale} />
          <div className="work-timeline__groups">
            {groups.map((group) => (
              <section className="timeline-group" key={group.id} aria-labelledby={`timeline-group-${group.id}`}>
                <header className="timeline-group__header"><h2 id={`timeline-group-${group.id}`}>{group.label}</h2><strong>{group.items.length}</strong><div aria-hidden="true" /></header>
                <div className="timeline-group__items">
                  {group.items.map((item) => {
                const itemProposals = proposals.filter((proposal) => proposal.workItemId === item.id);
                const agentState = agentStates.find((state) => state.workItemId === item.id) ?? null;
                const actionLabel = actionReadyLabel(itemProposals) ?? agentStateLabel(agentState);
                const placement = timelineRangePlacement(item, board.workspace.timezone, scale);
                const itemFeedback = currentWorkMutationFeedback(mutationFeedback, item.id);
                const selected = focusedWorkItemId === item.id;
                return (
                  <article className="timeline-task" data-work-id={item.id} data-entity-color={timelineClientColor(item.clientId, item.projectId)} data-selected={selected ? "true" : undefined} data-agent-mutation-stage={itemFeedback?.stage} data-approval-state={item.approvalStatus} key={item.id}>
                    <div className="timeline-task__property">
                      <ColorMark className="timeline-task__rule" color={timelineClientColor(item.clientId, item.projectId)} />
                      <button className="timeline-task__open" type="button" aria-current={selected ? "true" : undefined} aria-label={`Open ${item.title}`} onClick={() => openWork(item)}>
                        <strong>{item.title}</strong>
                        <small>{clientName(board, item)}{projectName(board, item) ? ` · ${projectName(board, item)}` : ""} · {memberName(board, item.assigneeId)}</small>
                      </button>
                      <div className="timeline-task__context"><span className={`table-status table-status--${item.status.toLowerCase()}`}>{STATUS_LABELS[item.status]}</span><span className={`timeline-task__priority timeline-task__priority--${item.priority.toLowerCase()}`}>{item.priority.toLowerCase()}</span><time dateTime={item.dueAt ?? undefined}>Due {timelineDateLabel(item.dueAt, board.workspace.timezone)}</time></div>
                      {item.filesReference || item.reviewFeedbackReference ? <div className="timeline-task__references" aria-label={`${item.title} references`}>
                        {item.filesReference ? <span><b>Files</b><SafeLinkedText text={item.filesReference} /></span> : null}
                        {item.reviewFeedbackReference ? <span><b>Review</b><SafeLinkedText text={item.reviewFeedbackReference} /></span> : null}
                      </div> : null}
                      <div className="timeline-task__signals">
                        <span className={`timeline-task__approval timeline-task__approval--${item.approvalStatus.toLowerCase()}`}>{APPROVAL_LABELS[item.approvalStatus]}</span>
                        {itemFeedback ? <WorkMutationBadge feedback={itemFeedback} /> : actionLabel ? <span className={`agent-ready-mark ${agentState ? `agent-ready-mark--${agentState.stage.toLowerCase()}` : ""}`}><i />{actionLabel}{itemProposals.length > 1 ? ` · ${itemProposals.length}` : ""}</span> : null}
                      </div>
                      <label className="timeline-task__status"><span>Status</span><select aria-label={`Move ${item.title} to status`} value={item.status} onChange={(event) => void moveWorkToStatus(item, event.target.value as WorkStatus, moveWork)}>{ACTIVE_STATUSES.map((status) => <option value={status} key={status}>{STATUS_LABELS[status]}</option>)}</select></label>
                    </div>
                    <div className={placement ? "timeline-task__track" : "timeline-task__track is-unscheduled"}>
                      <TimelineTrackGuides scale={scale} />
                      {placement
                        ? <TimelineRangeButton placement={placement} color={timelineClientColor(item.clientId, item.projectId)} label={`${item.title}: ${timelineDateLabel(item.taskStartAt ?? null, board.workspace.timezone)} to ${timelineDateLabel(item.taskEndAt ?? null, board.workspace.timezone)}`} open={() => openWork(item)} />
                        : <button type="button" className="timeline-task__no-range" onClick={() => openWork(item)}>No Timeline range</button>}
                    </div>
                  </article>
                );
                  })}
                </div>
              </section>
            ))}
          </div>
        </TimelineCanvas>
      </div>
    </section>
  );
}

function TimelineAxis({ scale }: { scale: TimelineScale }) {
  return (
    <div className="timeline-axis" aria-label="Task range scale">
      <strong>Task / range</strong>
      <div className="timeline-axis__track">
        {scale.ticks.map((tick, index) => <TimelinePosition className="timeline-axis__tick" position={tick.position} edge={index === 0 ? "start" : index === scale.ticks.length - 1 ? "end" : undefined} key={tick.ordinal}><i /><small>{tick.label}</small></TimelinePosition>)}
        <TimelinePosition className="timeline-axis__today" position={scale.todayPosition}><i /><small>Today</small></TimelinePosition>
      </div>
    </div>
  );
}

function TimelineCanvas({ scale, children }: { scale: TimelineScale; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.style.setProperty("--timeline-track-width", `${scale.contentWidth}px`);
  }, [scale.contentWidth]);
  return <div className="timeline-canvas" ref={ref}>{children}</div>;
}

function TimelineTrackGuides({ scale }: { scale: TimelineScale }) {
  return <span className="timeline-track-guides" aria-hidden="true">{scale.ticks.map((tick) => <TimelinePosition className="timeline-track-guide" position={tick.position} key={tick.ordinal}><i /></TimelinePosition>)}<TimelinePosition className="timeline-track-today" position={scale.todayPosition}><i /></TimelinePosition></span>;
}

function TimelineRangeButton({ placement, color, label, open }: { placement: TimelineRangePlacement; color: string; label: string; open: () => void }) {
  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    ref.current?.style.setProperty("--timeline-position", `${placement.startPosition}%`);
    ref.current?.style.setProperty("--timeline-width", `${placement.width}%`);
    ref.current?.style.setProperty("--timeline-client-color", color);
  }, [color, placement.startPosition, placement.width]);
  const visibleLabel = placement.outlier === "BEFORE"
    ? `Earlier · ${label}`
    : placement.outlier === "AFTER"
      ? `Later · ${label}`
      : label;
  return <button
    ref={ref}
    className="timeline-task__range"
    type="button"
    data-clipped-start={placement.clippedStart ? "true" : undefined}
    data-clipped-end={placement.clippedEnd ? "true" : undefined}
    data-outlier={placement.outlier?.toLowerCase()}
    aria-label={`Open ${label}`}
    onClick={open}
  ><span>{visibleLabel}</span></button>;
}

function TimelinePosition({ className, position, color, edge, children }: {
  className: string;
  position: number;
  color?: string;
  edge?: "start" | "end" | undefined;
  children: ReactNode;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    ref.current?.style.setProperty("--timeline-position", `${position}%`);
    if (color) ref.current?.style.setProperty("--timeline-client-color", color);
  }, [color, position]);
  return <span ref={ref} className={className} data-edge={edge}>{children}</span>;
}

function timelineDateLabel(value: string | null, timeZone: string): string {
  if (!value) return "No deadline";
  return new Intl.DateTimeFormat(undefined, { timeZone, weekday: "short", month: "short", day: "numeric" }).format(new Date(value));
}

function WorkMutationBadge({ feedback }: { feedback: WorkMutationFeedback }) {
  return <span className={`work-mutation-badge work-mutation-badge--${feedback.stage.toLowerCase()}`} title={feedback.failure ?? undefined} role="status" aria-live="polite"><i />{feedback.stage}</span>;
}

/**
 * WHY [BOL-CURRENT-UNLINKED-ACTION-FIRST-001]: a real ambiguity cannot own a
 * Work item until the missing destination is answered. It was therefore buried
 * behind 18 older unlinked rules even though Needs action counted it. Show one
 * current communication decision first, then keep the remaining queue one at a
 * time. Risk still controls approval safeguards; it must not make an old policy
 * review hide a live customer/team question. Falsifier: a ready clarification
 * is collapsed, absent, or ordered behind a non-message rule.
 */
export function prioritizeUnlinkedProposals(proposals: readonly AgentProposal[]): AgentProposal[] {
  const actionRank: Readonly<Record<AgentProposal["type"], number>> = {
    CLARIFICATION_MESSAGE: 0,
    ANSWER_MESSAGE: 1,
    FOLLOWUP_MESSAGE: 2,
    ESCALATION_MESSAGE: 2,
    NEW_WORK: 3,
    BOARD_UPDATE: 4,
    BUSINESS_RULE_UPDATE: 5
  };
  return [...proposals].sort((left, right) =>
    actionRank[left.type] - actionRank[right.type]
    || Date.parse(left.expiresAt) - Date.parse(right.expiresAt)
    || Date.parse(right.createdAt) - Date.parse(left.createdAt)
    || left.id.localeCompare(right.id));
}

interface AgentFocusItem {
  item: WorkItem;
  proposals: readonly AgentProposal[];
  agentState: WorkAgentState | null;
  attention: "PROPOSAL" | "IDENTITY_DECISION" | "DELIVERY_CHECK" | "WAITING";
  relatedCount: number;
}

// WHY [BOL-AGENT-FIRST-WORK-001]: the operator's default job is approving prepared
// work, not scanning every lane for an orange badge. Keep one row per owning
// task and leave the complete board one click away on this same Work surface.
// Falsifier: a task with a ready proposal is absent here or appears once per
// proposal and makes the review queue noisier than the work itself.
export function agentFocusItems(
  workItems: readonly WorkItem[],
  proposals: readonly AgentProposal[],
  agentStates: readonly WorkAgentState[] = []
): AgentFocusItem[] {
  const byWork = new Map<string, AgentProposal[]>();
  for (const proposal of proposals) {
    if (!proposal.workItemId) continue;
    const current = byWork.get(proposal.workItemId) ?? [];
    current.push(proposal);
    byWork.set(proposal.workItemId, current);
  }
  const statesByWork = new Map(agentStates.map((state) => [state.workItemId, state]));
  const identityCounts = new Map<string, number>();
  for (const proposal of proposals) {
    if (proposalAttentionKind(proposal) !== "IDENTITY_DECISION") continue;
    const memberId = proposal.targetRoute?.memberId;
    if (memberId) identityCounts.set(memberId, (identityCounts.get(memberId) ?? 0) + 1);
  }
  const claimedIdentityDecisions = new Set<string>();
  return [...workItems]
    .sort((left, right) => focusScore(right) - focusScore(left))
    .flatMap((item): AgentFocusItem[] => {
      const itemProposals = byWork.get(item.id) ?? [];
      const ready = itemProposals.filter((proposal) => proposalAttentionKind(proposal) === "READY");
      if (ready.length > 0) {
        return [{ item, proposals: ready, agentState: null, attention: "PROPOSAL", relatedCount: ready.length }];
      }
      const identity = itemProposals.find((proposal) => {
        if (proposalAttentionKind(proposal) !== "IDENTITY_DECISION") return false;
        const memberId = proposal.targetRoute?.memberId;
        return Boolean(memberId && !claimedIdentityDecisions.has(memberId));
      });
      if (identity?.targetRoute?.memberId) {
        claimedIdentityDecisions.add(identity.targetRoute.memberId);
        return [{
          item,
          proposals: [identity],
          agentState: null,
          attention: "IDENTITY_DECISION",
          relatedCount: identityCounts.get(identity.targetRoute.memberId) ?? 1
        }];
      }
      const agentState = statesByWork.get(item.id) ?? null;
      return agentState
        ? [{ item, proposals: [], agentState, attention: "DELIVERY_CHECK", relatedCount: 1 }]
        : [];
    });
}

export function agentWaitingItems(
  workItems: readonly WorkItem[],
  proposals: readonly AgentProposal[],
  agentStates: readonly WorkAgentState[]
): AgentFocusItem[] {
  const actionWorkIds = new Set(agentFocusItems(
    workItems,
    proposals,
    agentStates.filter((state) => ["DELIVERY_NEEDS_CHECK", "SCHEDULE_NEEDS_REVIEW"].includes(state.stage))
  ).map((item) => item.item.id));
  const proposalsByWork = new Map<string, AgentProposal[]>();
  for (const proposal of proposals) {
    if (!proposal.workItemId || proposalAttentionKind(proposal) === "READY") continue;
    const current = proposalsByWork.get(proposal.workItemId) ?? [];
    current.push(proposal);
    proposalsByWork.set(proposal.workItemId, current);
  }
  const waitingByWork = new Map<string, WorkAgentState>();
  for (const state of agentStates) {
    if (!["SCHEDULED", "FINAL_CHECKS", "WAITING_FOR_REPLY"].includes(state.stage) || actionWorkIds.has(state.workItemId)) continue;
    waitingByWork.set(state.workItemId, state);
  }
  return workItems
    .filter((item) => !actionWorkIds.has(item.id) && (waitingByWork.has(item.id) || proposalsByWork.has(item.id)))
    .map((item) => ({
      item,
      proposals: proposalsByWork.get(item.id) ?? [],
      agentState: waitingByWork.get(item.id) ?? null,
      attention: "WAITING" as const,
      relatedCount: proposalsByWork.get(item.id)?.length ?? 1
    }))
    .sort((left, right) => {
      const stageDelta = waitingStageScore(right.agentState) - waitingStageScore(left.agentState);
      return stageDelta || focusScore(right.item) - focusScore(left.item);
    });
}

export function workActionCount(board: BoardSnapshot, proposals: readonly AgentProposal[]): number {
  const workIds = new Set(board.workItems.map((item) => item.id));
  const unlinked = proposals.filter((proposal) => !proposal.workItemId || !workIds.has(proposal.workItemId)).length;
  const agentChecks = (board.workAgentStates ?? []).filter((state) => ["DELIVERY_NEEDS_CHECK", "SCHEDULE_NEEDS_REVIEW"].includes(state.stage));
  return agentFocusItems(board.workItems, proposals, agentChecks).length + unlinked;
}

function AgentFocusList({ items, board, openWork, showAll, empty }: {
  items: readonly AgentFocusItem[];
  board: BoardSnapshot;
  openWork: (item: WorkItem) => void;
  showAll: () => void;
  empty: string;
}) {
  if (!items.length) return <section className="work-focus-empty"><EmptyState>{empty}</EmptyState><button className="secondary-button" type="button" onClick={showAll}>See all work</button></section>;
  return (
    <section className="work-focus-list" aria-label={`${items.length} current work items`}>
      {items.map(({ item, proposals, agentState, attention, relatedCount }) => (
        <button className="work-focus-row" type="button" key={item.id} onClick={() => openWork(item)}>
          <ColorMark className="work-focus-row__rule" color={clientColor(board, item)} />
          <span className="work-focus-row__main"><strong>{item.title}</strong><small>{clientName(board, item)} · {memberName(board, item.assigneeId)}</small></span>
          <span className={`due-label due-label--${dueState(item).toLowerCase()}`}><CalendarClock size={14} />{formatDeadline(item.dueAt, { short: true })}</span>
          <span className={`work-focus-row__action ${agentState ? `work-focus-row__action--${agentState.stage.toLowerCase()}` : ""}`}><i />{focusActionLabel({ attention, relatedCount, proposals, agentState, board })}</span>
          <ArrowRight size={17} aria-hidden="true" />
        </button>
      ))}
    </section>
  );
}

/**
 * WHY [BOL-PHONE-APPROVAL-LIST-001]: rendering the full first proposal made
 * the phone home feel like a form and hid the rest of the queue. Keep every
 * prepared action in one compact, honest list and open its existing guarded
 * ProposalCard only after the operator chooses it. Falsifier: an unlinked action is
 * hidden, executes from this row, or the first screen again expands a full
 * editor before the operator chooses an approval.
 */
function UnlinkedApprovalList({ proposals, open }: {
  proposals: readonly AgentProposal[];
  open: (proposal: AgentProposal) => void;
}) {
  return (
    <section className="work-focus-list" aria-label={`${proposals.length} prepared approvals`}>
      {proposals.map((proposal) => (
        <button className="work-focus-row work-focus-row--proposal" type="button" key={proposal.id} onClick={() => open(proposal)}>
          <span className="work-focus-row__rule work-focus-row__rule--proposal" aria-hidden="true" />
          <span className="work-focus-row__main"><strong>{proposal.title}</strong><small>{proposalSourceSummary(proposal)}</small></span>
          <span className="work-focus-row__action"><i />{proposalReviewLabel(proposal)}</span>
          <ArrowRight size={17} aria-hidden="true" />
        </button>
      ))}
    </section>
  );
}

function proposalSourceSummary(proposal: AgentProposal): string {
  const source = proposal.evidence[0];
  if (source?.conversationLabel && source.senderLabel) return `${source.conversationLabel} · ${source.senderLabel}`;
  if (source?.conversationLabel) return source.conversationLabel;
  if (proposal.destinationLabel) return proposal.destinationLabel;
  return proposal.whyNow;
}

function proposalReviewLabel(proposal: AgentProposal): string {
  if (["FOLLOWUP_MESSAGE", "ESCALATION_MESSAGE", "ANSWER_MESSAGE", "CLARIFICATION_MESSAGE"].includes(proposal.type)) return "Review message";
  if (proposal.type === "NEW_WORK") return "Review task";
  if (proposal.type === "BOARD_UPDATE") return "Review update";
  return "Review rule";
}

function focusActionLabel(input: {
  attention: AgentFocusItem["attention"];
  relatedCount: number;
  proposals: readonly AgentProposal[];
  agentState: WorkAgentState | null;
  board: BoardSnapshot;
}): string {
  if (input.attention === "IDENTITY_DECISION") {
    const memberId = input.proposals[0]?.targetRoute?.memberId ?? null;
    const name = memberName(input.board, memberId);
    return `Confirm ${name}${input.relatedCount > 1 ? ` · ${input.relatedCount} drafts` : ""}`;
  }
  if (input.attention === "WAITING" && input.proposals[0]) return waitingProposalLabel(input.proposals[0]);
  const proposalLabel = actionReadyLabel(input.proposals);
  if (proposalLabel) return `${proposalLabel}${input.relatedCount > 1 ? ` · ${input.relatedCount}` : ""}`;
  return agentStateLabel(input.agentState) ?? "Check this task";
}

export function waitingProposalLabel(proposal: AgentProposal): string {
  const state = proposal.targetRoute?.state;
  if (state === "NOT_IN_GROUP") return "Waiting for correct group";
  if (state === "IDENTITY_REVIEW") return "Identity decision pending";
  if (state === "IDENTITY_AMBIGUOUS" || state === "IDENTITY_CONFLICT") return "Identity needs clarification";
  if (state === "IDENTITY_UNRESOLVED") return "Waiting for identity";
  if (state === "IDENTITY_SYNCING" || state === "MEMBERSHIP_UNKNOWN") return "Checking WhatsApp";
  if (state === "ROUTE_MISSING" || proposal.conversationId === null) return "Waiting for correct group";
  return "Waiting on a safety check";
}

function waitingStageScore(state: WorkAgentState | null): number {
  return state?.stage === "SCHEDULED" ? 3 : state?.stage === "FINAL_CHECKS" ? 2 : state?.stage === "WAITING_FOR_REPLY" ? 1 : 0;
}

export function agentStateLabel(state: WorkAgentState | null | undefined): string | null {
  if (state?.stage === "SCHEDULED") return state.scheduledAt ? `Scheduled · ${formatScheduleShort(state.scheduledAt, state.scheduleTimeZone)}` : "Scheduled";
  if (state?.stage === "SCHEDULE_NEEDS_REVIEW") return "Schedule needs review";
  if (state?.stage === "FINAL_CHECKS") return "Final safety check";
  if (state?.stage === "WAITING_FOR_REPLY") return "Waiting for reply";
  if (state?.stage === "MESSAGE_SENT") {
    if (state.deliveryState === "PLAYED") return "Played";
    if (state.deliveryState === "READ") return "Read";
    if (state.deliveryState === "DELIVERED") return "Delivered";
    return "Sent";
  }
  if (state?.stage === "DELIVERY_NEEDS_CHECK") return "Delivery needs checking";
  return null;
}

function workModeTitle(mode: WorkMode, actionCount: number, waitingCount: number, activeCount: number, loadState?: AgentWorkQueue["loadState"]): string {
  if (mode === "action" && loadState === "LOADING") return "Checking what needs your approval";
  if (mode === "action" && loadState === "STALE" && actionCount === 0) return "Approvals are unavailable";
  if (mode === "action") return "What needs your approval?";
  if (mode === "waiting") return waitingCount ? `${waitingCount} ${waitingCount === 1 ? "item is" : "items are"} being handled` : "Nothing is waiting";
  return `Board · ${activeCount} active ${activeCount === 1 ? "task" : "tasks"}`;
}

function workModeDescription(mode: WorkMode): string {
  if (mode === "action") return "The agent prepares the details. Check each item, edit if needed, then approve.";
  if (mode === "waiting") return "The agent is waiting for a reply, identity, correct group, or final safety check.";
  return "The same tasks shown in Approvals and Calendar, arranged by status.";
}

function focusScore(item: WorkItem): number {
  return (item.status === "BLOCKED" ? 50 : 0) + ({ OVERDUE: 40, TODAY: 30, SOON: 20, LATER: 0, NONE: 0 }[dueState(item)]) + (item.priority === "URGENT" ? 15 : item.priority === "HIGH" ? 8 : 0);
}

function WorkColumn({ lane, items, board, openWork, moveWork, mobileActive, proposals, agentStates, focusedWorkItemId, mutationFeedback }: {
  lane: WorkLane;
  items: readonly WorkItem[];
  board: BoardSnapshot;
  openWork: (item: WorkItem) => void;
  moveWork: (item: WorkItem, status: WorkStatus) => Promise<void>;
  mobileActive: boolean;
  proposals: readonly AgentProposal[];
  agentStates: readonly WorkAgentState[];
  focusedWorkItemId: string | null;
  mutationFeedback: readonly WorkMutationFeedback[];
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `lane:${lane.id}` });
  return (
    <section ref={setNodeRef} id={`work-status-panel-${lane.id}`} role="tabpanel" aria-labelledby={`work-status-tab-${lane.id}`} tabIndex={mobileActive ? 0 : -1} className={`board-column board-column--${lane.id} ${isOver ? "is-over" : ""} ${mobileActive ? "is-mobile-active" : ""}`} aria-label={`${lane.label} work`}>
      <header className="board-column__header"><span className="status-dot" /><h2>{lane.label}</h2><strong>{items.length}</strong></header>
      <div className="board-column__body">
        {items.length === 0 ? <div className="column-empty">Drop work here</div> : items.map((item) => <WorkCard key={item.id} item={item} board={board} open={() => openWork(item)} moveWork={moveWork} proposals={proposals.filter((proposal) => proposal.workItemId === item.id)} agentState={agentStates.find((state) => state.workItemId === item.id) ?? null} selected={focusedWorkItemId === item.id} mutationFeedback={currentWorkMutationFeedback(mutationFeedback, item.id)} />)}
      </div>
    </section>
  );
}

function WorkCard({ item, board, open, moveWork, proposals, agentState, selected, mutationFeedback }: { item: WorkItem; board: BoardSnapshot; open: () => void; moveWork: (item: WorkItem, status: WorkStatus) => Promise<void>; proposals: readonly AgentProposal[]; agentState: WorkAgentState | null; selected: boolean; mutationFeedback: WorkMutationFeedback | null }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: item.id });
  const cardRef = useRef<HTMLElement | null>(null);
  const attachCard = useCallback((node: HTMLElement | null) => {
    cardRef.current = node;
    setNodeRef(node);
  }, [setNodeRef]);
  useEffect(() => {
    const node = cardRef.current;
    if (!node) return;
    node.style.setProperty("--drag-x", transform ? `${Math.round(transform.x)}px` : "0px");
    node.style.setProperty("--drag-y", transform ? `${Math.round(transform.y)}px` : "0px");
  }, [transform]);
  const due = dueState(item);
  const owner = memberName(board, item.assigneeId);
  const actionLabel = actionReadyLabel(proposals) ?? agentStateLabel(agentState);
  return (
    <article ref={attachCard} data-work-id={item.id} data-entity-color={timelineClientColor(item.clientId, item.projectId)} data-selected={selected ? "true" : undefined} data-agent-mutation-stage={mutationFeedback?.stage} className={`work-card ${isDragging ? "is-dragging" : ""} ${item.priority === "URGENT" ? "is-urgent" : ""}`}>
      <ColorMark className="work-card__rule" color={timelineClientColor(item.clientId, item.projectId)} />
      <div className="work-card__top"><span><span className="client-label">{clientName(board, item)}</span><small className="work-card__stage">{STATUS_LABELS[item.status]}</small></span><button type="button" className="drag-handle" {...listeners} {...attributes} aria-label={`Move ${item.title}`}><GripVertical size={16} /></button></div>
      <button type="button" className="work-card__open" aria-current={selected ? "true" : undefined} onClick={open}><h3>{item.title}</h3>{projectName(board, item) ? <p>{projectName(board, item)}</p> : null}{mutationFeedback ? <WorkMutationBadge feedback={mutationFeedback} /> : actionLabel ? <span className={`agent-ready-mark ${agentState ? `agent-ready-mark--${agentState.stage.toLowerCase()}` : ""}`}><i />{actionLabel}{proposals.length > 1 ? ` · ${proposals.length}` : ""}</span> : null}</button>
      <label className="work-card__status-move"><span>Move to</span><select aria-label={`Move ${item.title} to status`} value={item.status} onChange={(event) => void moveWorkToStatus(item, event.target.value as WorkStatus, moveWork)}>{ACTIVE_STATUSES.map((status) => <option value={status} key={status}>{STATUS_LABELS[status]}</option>)}</select></label>
      {item.status === "BLOCKED" && item.blockedReason ? <p className="blocker-line">{item.blockedReason}</p> : null}
      <footer className="work-card__footer"><Avatar name={owner} size="small" /><span className={`due-label due-label--${due.toLowerCase()}`}><CalendarClock size={14} />{formatDeadline(item.dueAt, { short: true })}</span>{item.manualLock ? <LockKeyhole size={14} aria-label="Automation locked" /> : null}</footer>
    </article>
  );
}

function WorkTable({ items, board, openWork, proposals, agentStates, focusedWorkItemId, mutationFeedback }: { items: readonly WorkItem[]; board: BoardSnapshot; openWork: (item: WorkItem) => void; proposals: readonly AgentProposal[]; agentStates: readonly WorkAgentState[]; focusedWorkItemId: string | null; mutationFeedback: readonly WorkMutationFeedback[] }) {
  if (!items.length) return <EmptyState>No work matches these filters.</EmptyState>;
  return (
    <div className="work-table-wrap"><table className="work-table"><thead><tr><th>Work</th><th>Client</th><th>Status</th><th>Assignee</th><th>Deadline</th><th>Priority</th></tr></thead><tbody>{items.map((item) => { const itemProposals = proposals.filter((proposal) => proposal.workItemId === item.id); const agentState = agentStates.find((state) => state.workItemId === item.id) ?? null; const actionLabel = actionReadyLabel(itemProposals) ?? agentStateLabel(agentState); const itemFeedback = currentWorkMutationFeedback(mutationFeedback, item.id); const selected = focusedWorkItemId === item.id; return <tr data-work-id={item.id} data-selected={selected ? "true" : undefined} data-agent-mutation-stage={itemFeedback?.stage} key={item.id} onClick={() => openWork(item)}><td><button type="button" aria-current={selected ? "true" : undefined} onClick={() => openWork(item)}><ColorMark className="table-client-rule" color={timelineClientColor(item.clientId, item.projectId)} /><span>{item.title}{itemFeedback ? <WorkMutationBadge feedback={itemFeedback} /> : actionLabel ? <small className={`agent-ready-mark ${agentState ? `agent-ready-mark--${agentState.stage.toLowerCase()}` : ""}`}><i />{actionLabel}</small> : null}</span></button></td><td>{clientName(board, item)}</td><td><span className={`table-status table-status--${item.status.toLowerCase()}`}>{STATUS_LABELS[item.status]}</span></td><td>{memberName(board, item.assigneeId)}</td><td><span className={`due-label due-label--${dueState(item).toLowerCase()}`}>{formatDeadline(item.dueAt, { short: true })}</span></td><td>{item.priority.toLowerCase()}</td></tr>; })}</tbody></table></div>
  );
}

// WHY [BOL-WORK-ACTION-SIGNAL-001]: agent work used to live on a separate
// page, then the vague "Follow-up ready" status still let an owner miss the
// actual editable draft. Keep one action-worded signal on the owning task and
// reveal the full draft only when that task opens. Falsifier: a ready proposal
// has no "Review draft" cue, or the board renders the full queue at once.
export function actionReadyLabel(proposals: readonly AgentProposal[]): string | null {
  const first = proposals[0];
  if (!first) return null;
  const attention = proposalAttentionKind(first);
  if (attention === "IDENTITY_DECISION") return "Confirm identity";
  if (attention === "WAITING") return waitingProposalLabel(first);
  if (first.effect.kind === "SEND_MESSAGE" && first.effect.schedule) return "Review schedule";
  if (["FOLLOWUP_MESSAGE", "ESCALATION_MESSAGE", "ANSWER_MESSAGE", "CLARIFICATION_MESSAGE"].includes(first.type)) return "Review draft";
  return first.type === "NEW_WORK" ? "New work ready" : "Update ready";
}

function formatScheduleShort(value: string, timeZone: string | null): string {
  return new Intl.DateTimeFormat(undefined, {
    ...(timeZone ? { timeZone } : {}),
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit"
  }).format(new Date(value));
}
